import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RATE_FALLBACK } from "@/lib/format";

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [currency, setCurrency] = useState("INR");
  const [rateMode, setRateModeState] = useState(
    () => localStorage.getItem("finhq_rate_mode") || "average"
  );
  const [rateAsOf, setRateAsOfState] = useState(
    () => localStorage.getItem("finhq_rate_as_of") || ""
  );
  const [rates, setRates] = useState(RATE_FALLBACK);

  useEffect(() => {
    // /api/rates requires auth — skip until the user is signed in to avoid a
    // noisy 401 on the login page.
    if (!authed) return;
    // Debounce rate re-fetch (esp. for date typing) to avoid a burst of 10 requests.
    const params = new URLSearchParams();
    if (rateMode) params.set("mode", rateMode);
    if (rateAsOf) params.set("as_of", rateAsOf);
    const t = setTimeout(() => {
      api
        .get(`/rates${params.toString() ? "?" + params.toString() : ""}`)
        .then(({ data }) => data?.rates && setRates(data.rates))
        .catch((e) => {
          console.error("Failed to load FX rates, using fallback", e);
        });
    }, 350);
    return () => clearTimeout(t);
  }, [authed, rateMode, rateAsOf]);

  const updateCurrency = useCallback((c) => {
    setCurrency(c);
  }, []);

  const setRateMode = useCallback((m) => {
    setRateModeState(m);
    localStorage.setItem("finhq_rate_mode", m);
  }, []);

  const setRateAsOf = useCallback((d) => {
    setRateAsOfState(d || "");
    if (d) localStorage.setItem("finhq_rate_as_of", d);
    else localStorage.removeItem("finhq_rate_as_of");
  }, []);

  const value = useMemo(
    () => ({ currency, setCurrency: updateCurrency, rates, rateMode, setRateMode, rateAsOf, setRateAsOf }),
    [currency, updateCurrency, rates, rateMode, setRateMode, rateAsOf, setRateAsOf]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export const useCurrency = () => useContext(CurrencyContext);
